import aiStudioService from "@apis/aiStudioService";
import { ProductImageResult, uploadProductImage } from "@apis/upload";
import { resolveTraceIdFromHeaders } from "@apis/http/traceId";
import { copyTextToClipboard } from "@kalodata/tools";
import { message } from "antd";
import { t } from "i18next";
import { useState } from "react";
import { compressImageToLimit, isLossyCompressibleImage } from "@utils/imageUpload/preprocess";
import report from "@report";

// ================== 图片上传相关常量 ==================
export const IMAGE_UPLOAD_CONFIG = {
  ACCEPTED_MIME_TYPES: [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/bmp",
    "image/gif",
    "image/webp",
    "image/heic",
    "image/heif",
  ] as const,
  ACCEPTED_EXTENSIONS: [".png", ".jpg", ".jpeg", ".bmp", ".gif", ".webp", ".heic", ".heif"],
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  COMPRESS_OUTPUT_TYPE: "image/jpeg",
  COMPRESS_MAX_WIDTH_OR_HEIGHT: 4096,
  MIN_PX: 100,
  MAX_PX: 4096,
  DEFAULT_ERROR_KEY: "Image upload failed, please refer to the requirements and re-upload",
} as const;

/** 后端 /api/video/upload-images 原生支持的 MIME 类型，其它格式由前端先压缩转换 */
const SERVER_NATIVELY_SUPPORTED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
]);
const SERVER_NATIVELY_SUPPORTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

/** 将字节数格式化为可读大小文案，如 100KB、5MB */
const formatFileSizeLabel = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${Math.round(bytes / (1024 * 1024))}MB`;
};

const getFileExtension = (fileName: string | undefined): string => {
  if (!fileName) return "";
  const match = fileName.toLowerCase().match(/\.[^.]+$/);
  return match?.[0] ?? "";
};

/** 判断文件是否后端原生支持，原生不支持的（bmp/heic/heif）需要先在前端转成 JPEG */
const isServerNativelySupportedFormat = (file: File): boolean => {
  const fileType = file.type?.toLowerCase();
  if (fileType && SERVER_NATIVELY_SUPPORTED_MIME_TYPES.has(fileType)) return true;
  // type 缺失（部分手机/截图导出）时退而求其次按扩展名判断
  if (!fileType) {
    const ext = getFileExtension(file.name);
    return SERVER_NATIVELY_SUPPORTED_EXTENSIONS.includes(ext);
  }
  return false;
};

// ================== 图片上传工具函数 ==================
/** 获取图片文件的原始尺寸 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.addEventListener("load", () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    });
    img.addEventListener("error", (err) => {
      reject(err);
      URL.revokeObjectURL(url);
    });
    img.src = url;
  });
};

/** 获取 base64 预览 */
export const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener("load", () => resolve(reader.result as string));
    reader.addEventListener("error", (error) => reject(error));
  });

/** 校验单个图片文件 */
interface ValidateImageFileOptions {
  maxSizeBytes?: number;
  skipSizeCheck?: boolean;
}

const validateSingleImageFile = async (file: File, options?: ValidateImageFileOptions): Promise<boolean> => {
  const { ACCEPTED_MIME_TYPES, ACCEPTED_EXTENSIONS, MAX_SIZE, MIN_PX, MAX_PX, DEFAULT_ERROR_KEY } = IMAGE_UPLOAD_CONFIG;
  const sizeLimit = options?.maxSizeBytes ?? MAX_SIZE;

  // 校验文件大小
  if (!options?.skipSizeCheck && file.size > sizeLimit) {
    message.error(t("File size exceeds {{limit}} limit", { limit: formatFileSizeLabel(sizeLimit) }));
    return false;
  }

  // 校验文件类型，双重验证文件类型和后缀
  const fileType = file.type?.toLowerCase();
  const fileName = file.name?.toLowerCase();
  const formatMatched =
    (fileType && ACCEPTED_MIME_TYPES.includes(fileType as (typeof ACCEPTED_MIME_TYPES)[number])) ||
    ACCEPTED_EXTENSIONS.some((ext) => fileName?.endsWith(ext));
  if (!formatMatched) {
    message.error(t("Unsupported image format"));
    return false;
  }

  // 校验图片尺寸
  try {
    const { width, height } = await getImageDimensions(file);
    if (width < MIN_PX || height < MIN_PX || width > MAX_PX || height > MAX_PX) {
      message.error(t("Image dimensions must be between 100px and 4096px"));
      return false;
    }
  } catch {
    message.error(t(DEFAULT_ERROR_KEY));
    return false;
  }

  return true;
};

/** 验证图片文件，支持单文件或文件列表（列表时逐个校验，第一个不通过即返回） */
export const validateImageFile = async (fileOrList: File | File[]): Promise<boolean> => {
  const files = Array.isArray(fileOrList) ? fileOrList : [fileOrList];
  for (const file of files) {
    const valid = await validateSingleImageFile(file);
    if (!valid) return false;
  }
  return true;
};

export interface PreprocessImageConfig {
  maxSizeBytes?: number;
  compressOutputType?: string;
  compressMaxWidthOrHeight?: number;
}

const getNormalizedFileName = (fileName: string, fileType: string): string => {
  const ext = fileType.split("/")[1] ?? "jpg";
  if (!/\.[^.]+$/.test(fileName)) return `${fileName}.${ext}`;
  return fileName.replace(/\.[^.]+$/, `.${ext}`);
};

const ensureUploadFileMetadata = (file: File, sourceName: string): File => {
  if (file.name && file.name !== "blob") return file;
  return new File([file], sourceName, {
    type: file.type,
    lastModified: Date.now(),
  });
};

/** 图片预处理：基础校验 -> （超限或格式不支持时）压缩 -> 二次校验，成功后返回可上传文件 */
export const preprocessImageFile = async (file: File, config?: PreprocessImageConfig): Promise<File | null> => {
  const maxSizeBytes = config?.maxSizeBytes ?? IMAGE_UPLOAD_CONFIG.MAX_SIZE;
  const compressOutputType = config?.compressOutputType ?? IMAGE_UPLOAD_CONFIG.COMPRESS_OUTPUT_TYPE;
  const compressMaxWidthOrHeight = config?.compressMaxWidthOrHeight ?? IMAGE_UPLOAD_CONFIG.COMPRESS_MAX_WIDTH_OR_HEIGHT;

  const isBaseValid = await validateSingleImageFile(file, { skipSizeCheck: true, maxSizeBytes });
  if (!isBaseValid) return null;

  const isOverSize = file.size > maxSizeBytes;
  const needsFormatNormalization = !isServerNativelySupportedFormat(file);

  // 既不超限也不需要格式归一化，直接返回原文件
  if (!isOverSize && !needsFormatNormalization) {
    return file;
  }

  if (!isLossyCompressibleImage(file)) {
    message.error(
      isOverSize
        ? t("File size exceeds {{limit}} limit", { limit: formatFileSizeLabel(maxSizeBytes) })
        : t("Unsupported image format"),
    );
    return null;
  }

  try {
    const compressedFile = await compressImageToLimit(file, {
      maxSizeBytes,
      maxWidthOrHeight: compressMaxWidthOrHeight,
      outputType: compressOutputType,
      // 后端不原生支持的格式（bmp/heic/heif），即使大小没超也强制走 JPEG 转换
      forceConvert: needsFormatNormalization,
    });
    const normalizedName = getNormalizedFileName(file.name, compressOutputType);
    const uploadReadyFile = ensureUploadFileMetadata(compressedFile, normalizedName);
    const isValidAfterCompress = await validateSingleImageFile(uploadReadyFile, { maxSizeBytes });
    if (!isValidAfterCompress) return null;
    return uploadReadyFile;
  } catch {
    message.error(t(IMAGE_UPLOAD_CONFIG.DEFAULT_ERROR_KEY));
    return null;
  }
};

export const preprocessImageFiles = async (files: File[], config?: PreprocessImageConfig): Promise<File[] | null> => {
  const processed: File[] = [];
  for (const file of files) {
    const processedFile = await preprocessImageFile(file, config);
    if (!processedFile) return null;
    processed.push(processedFile);
  }
  return processed;
};

// ================== 上传埋点 ==================
const UPLOAD_DIAGNOSTIC_EVENT = "product_image_upload_diagnostic";
const UPLOAD_ENDPOINT = "/api/video/upload-images";

type UploadDiagnosticStage =
  | "selected"
  | "preprocess_success"
  | "preprocess_failed"
  | "upload_success"
  | "upload_business_failed"
  | "upload_http_error";

interface UploadDiagnosticPayload {
  endpoint: string;
  nameExt: string;
  fileType: string;
  size: number;
  width?: number;
  height?: number;
  lastModifiedAgeMs?: number;
  preprocess?: {
    wasCompressed: boolean;
    wasFormatConverted: boolean;
    originalSize: number;
    processedSize: number;
    originalType: string;
    processedType: string;
  };
  request?: {
    status?: number | string;
    code?: string | number;
    message?: string;
    traceId?: string;
    elapsedMs?: number;
    axiosCode?: string;
  };
  business?: {
    rejectionReason?: string;
    successCount?: number;
    failedCount?: number;
  };
  env: {
    userAgent: string;
    platform: string;
    language: string;
    isMobileViewport: boolean;
  };
}

const collectEnvInfo = (): UploadDiagnosticPayload["env"] => ({
  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  platform: typeof navigator !== "undefined" ? navigator.platform : "",
  language: typeof navigator !== "undefined" ? navigator.language : "",
  isMobileViewport: typeof window !== "undefined" ? window.innerWidth < 768 : false,
});

const buildBaseDiagnosticPayload = async (
  file: File,
  options?: { skipDimensions?: boolean },
): Promise<UploadDiagnosticPayload> => {
  let width: number | undefined;
  let height: number | undefined;
  if (!options?.skipDimensions) {
    try {
      const dim = await getImageDimensions(file);
      width = dim.width;
      height = dim.height;
    } catch {
      // 取不到尺寸不影响埋点
    }
  }
  return {
    endpoint: UPLOAD_ENDPOINT,
    nameExt: getFileExtension(file.name),
    fileType: file.type ?? "",
    size: file.size,
    width,
    height,
    lastModifiedAgeMs: file.lastModified ? Date.now() - file.lastModified : undefined,
    env: collectEnvInfo(),
  };
};

const reportUploadDiagnostic = (stage: UploadDiagnosticStage, payload: UploadDiagnosticPayload) => {
  try {
    report.event(UPLOAD_DIAGNOSTIC_EVENT, {
      kalo_pos: UPLOAD_DIAGNOSTIC_EVENT,
      kalo_type: stage,
      kalo_value: payload.nameExt,
      kalo_json: payload,
    });
  } catch {
    // 埋点失败不影响主流程
  }
};

// ================== 错误 toast ==================
/** 展示带 Trace ID 的图片上传错误 toast，trace id 可点击复制；trace id 为空时退化为普通 toast */
export const showImageUploadErrorToast = (msgKey: string, traceId?: string) => {
  const localizedMessage = t(msgKey);
  if (!traceId) {
    message.error({ content: localizedMessage, duration: 5 });
    return;
  }

  const copyTraceId = () => {
    copyTextToClipboard(traceId)
      .then(() => message.success(t("Copied")))
      .catch(() => {});
  };

  message.error({
    content: (
      <span className="inline-flex flex-col">
        <span>{localizedMessage}</span>
        <span className="select-all cursor-pointer" title={t("Click to copy")} onClick={copyTraceId}>
          {`Trace ID: ${traceId}`}
        </span>
      </span>
    ),
    duration: 5,
  });
};

// ================== 图片上传 Hook ==================
export interface UseImageUploadOptions {
  /** 上传成功回调 */
  onSuccess?: (result: ProductImageResult[]) => void;
  /** 上传失败回调（业务/HTTP 失败都会触发；toast 已由 hook 自行展示，调用方不要再重复弹出） */
  onError?: (error: unknown) => void;
}

export type UploadStatus = "idle" | "uploading" | "success" | "error";

/** 从 axios 错误对象中抽取 status/code/message/traceId/axiosCode，用于埋点和 toast */
export const extractAxiosErrorMeta = (error: unknown) => {
  const err = error as {
    response?: {
      status?: number;
      data?: { code?: string | number; message?: string };
      headers?: Record<string, string>;
    };
    code?: string;
    message?: string;
    traceID?: string;
    traceId?: string;
  };
  const headerTraceId = resolveTraceIdFromHeaders(err.response?.headers as Record<string, string> | undefined);
  return {
    status: err.response?.status,
    code: err.response?.data?.code ?? err.code,
    message: err.response?.data?.message ?? err.message,
    traceId: err.traceId || err.traceID || headerTraceId,
    axiosCode: err.code,
  };
};

export const useImageUpload = (options?: UseImageUploadOptions) => {
  const [status, setStatus] = useState<UploadStatus>("idle");

  /** 上传图片 */
  const uploadImage = async (file: File): Promise<ProductImageResult[] | null> => {
    setStatus("uploading");

    // selected 阶段不取尺寸，避免某些浏览器无法解码 HEIC/HEIF 时阻塞主流程
    const basePayload = await buildBaseDiagnosticPayload(file, { skipDimensions: true });
    reportUploadDiagnostic("selected", basePayload);

    const preprocessedFile = await preprocessImageFile(file, {
      compressOutputType: IMAGE_UPLOAD_CONFIG.COMPRESS_OUTPUT_TYPE,
    });
    if (!preprocessedFile) {
      setStatus("error");
      reportUploadDiagnostic("preprocess_failed", basePayload);
      options?.onError?.(new Error("Image preprocess failed"));
      return null;
    }

    // 预处理后基本可以稳定取到尺寸（HEIC/HEIF 已经被转换为 JPEG）
    const enrichedPayload = await buildBaseDiagnosticPayload(preprocessedFile);
    const preprocessMeta: UploadDiagnosticPayload["preprocess"] = {
      wasCompressed: preprocessedFile.size !== file.size,
      wasFormatConverted: (preprocessedFile.type || "").toLowerCase() !== (file.type || "").toLowerCase(),
      originalSize: file.size,
      processedSize: preprocessedFile.size,
      originalType: file.type ?? "",
      processedType: preprocessedFile.type ?? "",
    };
    reportUploadDiagnostic("preprocess_success", { ...enrichedPayload, preprocess: preprocessMeta });

    const startedAt = Date.now();
    try {
      const result = await uploadProductImage(preprocessedFile);
      const elapsedMs = Date.now() - startedAt;

      // 后端兼容字段：业务失败可能体现为 success=false 或缺少 imageUrl
      const failedItem = result.find((item) => !item.success || !item.imageUrl);
      if (failedItem) {
        setStatus("error");
        reportUploadDiagnostic("upload_business_failed", {
          ...enrichedPayload,
          preprocess: preprocessMeta,
          request: { status: 200, elapsedMs },
          business: {
            rejectionReason: failedItem.rejectionReason,
            successCount: result.filter((item) => item.success && item.imageUrl).length,
            failedCount: result.filter((item) => !item.success || !item.imageUrl).length,
          },
        });
        const reason = failedItem.rejectionReason;
        if (reason) {
          message.error({ content: reason, duration: 5 });
        } else {
          showImageUploadErrorToast(IMAGE_UPLOAD_CONFIG.DEFAULT_ERROR_KEY);
        }
        options?.onError?.(new Error(reason || "Image upload rejected"));
        return null;
      }

      setStatus("success");
      reportUploadDiagnostic("upload_success", {
        ...enrichedPayload,
        preprocess: preprocessMeta,
        request: { status: 200, elapsedMs },
      });
      options?.onSuccess?.(result);
      return result;
    } catch (error) {
      setStatus("error");
      const elapsedMs = Date.now() - startedAt;
      const meta = extractAxiosErrorMeta(error);
      reportUploadDiagnostic("upload_http_error", {
        ...enrichedPayload,
        preprocess: preprocessMeta,
        request: {
          status: meta.status,
          code: meta.code,
          message: meta.message,
          traceId: meta.traceId,
          elapsedMs,
          axiosCode: meta.axiosCode,
        },
      });
      showImageUploadErrorToast(IMAGE_UPLOAD_CONFIG.DEFAULT_ERROR_KEY, meta.traceId);
      options?.onError?.(error);
      return null;
    }
  };

  /** 重置状态 */
  const resetStatus = () => setStatus("idle");

  return {
    status,
    setStatus,
    uploadImage,
    resetStatus,
    // 导出工具函数方便组件内使用
    getBase64,
    validateImageFile,
    preprocessImageFile,
    preprocessImageFiles,
  };
};

export const useInsight = () => {
  const [videoInsight, setVideoInsight] = useState<any>({ status: "idle", data: null, error: null });

  const handleVideoInsight = async (videoLink: string) => {
    setVideoInsight({ status: "loading", data: null });

    try {
      const data: any = await aiStudioService.fetchVideoLinkPreview({
        videoLink,
      });
      setVideoInsight({ status: "succeeded", data });
      return data;
    } catch (error: any) {
      setVideoInsight({ status: "failed", data: null, error: error?.message });
      message.error((error?.message as any) || "Video link not found, Please re-enter it.");
    }
  };

  return { videoInsight, setVideoInsight, handleVideoInsight };
};
